import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import * as planningApi from '../../../lib/api/v2/planning'
import type { ClientOrderDto, SupplierOrderLineDto } from '../../../lib/api/v2/planning'
import { Skeleton } from '../../ui/skeleton'
import { computeStatusCounts, readiness } from './sidebar/supplier-orders-helpers'
import { deriveCommandesPhase } from './sidebar/supplier-orders-phase'
import { isCommandable, reconcileClientOrder } from './client-order-helpers'

interface ClientOrderPickerProps {
  projectId: string
}

const frDate = (iso: string) => {
  const d = new Date(iso)
  // A malformed marker must not print « confirmé le Invalid Date » on a client's project file.
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** The status breakdown, built as entries so the separator sits BETWEEN items and never leads. */
function breakdownEntries(counts: ReturnType<typeof computeStatusCounts>): [string, number][] {
  return ([
    ['À commander', counts.toOrder],
    ['En attente', counts.onHold],
    ['Commandées', counts.ordered],
    ['Confirmées', counts.confirmed],
    ['Reçues', counts.received],
  ] as [string, number][]).filter(([, n]) => n > 0)
}

/**
 * Project-page MIRROR of the supplier ordering state (SOLY-PO-001 — read-only since UX round 2):
 * the definition gesture lives in the registre (one writing surface,
 * `/planning/projects/:id/commandes`) and the tracking lives in the Planning. This section answers
 * « où en est-on ? » from the project file, and routes to the registre for any change.
 *
 * It used to answer that question by reciting the inventory: one row per client order line, no cap,
 * every kind included — so a discount or a note appeared tagged « non suivie » on a line that can
 * never be tracked. On a kitchen at 80 lines that is ~3 500px of scrolling to learn a number.
 * (Kitchy Bell', 2026-09: « too long and useless, we need to scroll indefinitely ».)
 *
 * It now renders the four phases the planning drawer renders, sharing `deriveCommandesPhase`. Zero
 * item rows, deliberately: DESIGN-CONTEXT §5.3 asks for « only the actionable lines, the rest folded
 * behind a counter », but that governs the surface where you ACT — the registre, which already
 * applies it. §5.2 asks this one for a diagnostic, and a diagnostic is a count and a name.
 *
 * ⚠️ The phase is derived from the SUPPLIER lines alone. That is why the untracked count below is
 * not optional: a project can have two received supplier lines and sixty orderable quote lines
 * nobody ever put on the list, and the phase alone would call that « tout reçu ». The old per-line
 * list showed those sixty as « non suivie », and it was the ONLY place in the product that did —
 * the alert engine compares tracked lines against each other, never against the client order. So
 * the aggregate has to carry the divergence itself or the rewrite loses information that mattered.
 * Amber, not red: DESIGN-CONTEXT §3.3 reserves red for risque/retard and gives divergence amber.
 */
export function ClientOrderPicker({ projectId }: ClientOrderPickerProps) {
  const [clientOrder, setClientOrder] = useState<ClientOrderDto | null>(null)
  const [lines, setLines] = useState<SupplierOrderLineDto[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    void (async () => {
      try {
        // Two LIGHT reads (review I5): the marker rides the client-order payload — the heavy
        // cockpit detail (alert engine) is never needed here.
        const [order, supplierLines] = await Promise.all([
          planningApi.getClientOrder(projectId),
          planningApi.getSupplierLines(projectId),
        ])
        if (!active) return
        setClientOrder(order)
        setLines(supplierLines)
        setFailed(false)
      } catch {
        if (!active) return
        // A failure must say failure. Faking an empty result here used to render nothing, which was
        // merely silent; under the phase model those exact values (no lines, no marker) derive to
        // `a-definir`, so a dropped request would render « Rien n'est encore trié » — an assertion
        // about the ordering state, made from data we never received, on the screen whose whole job
        // is that nothing gets forgotten.
        setFailed(true)
        setClientOrder(null)
        setLines(null)
      }
    })()
    return () => { active = false }
  }, [projectId, reloadKey])

  // Clearing here rather than at the top of the effect: an event handler may setState, an effect
  // body may not (react-hooks/set-state-in-effect). It also gives the retry visible feedback — the
  // skeleton returns while the second read is in flight. A CHANGE of project is handled the other
  // way, by `key={id}` at the call site, which remounts with fresh state.
  const retry = useCallback(() => {
    setFailed(false)
    setClientOrder(null)
    setLines(null)
    setReloadKey((k) => k + 1)
  }, [])

  // One pass over the data per data change, not four per render. `reconcileClientOrder` is pure, so
  // this is a cost fix and not a correctness one — but it also builds a `manual` list this surface
  // never reads, and `deriveCommandesPhase` recomputes the counts internally.
  const derived = useMemo(() => {
    if (!clientOrder || !lines) return null
    const counts = computeStatusCounts(lines)
    const nothingToOrderAt = clientOrder.nothingToOrderAt
    return {
      counts,
      ready: readiness(counts),
      nothingToOrderAt,
      phase: deriveCommandesPhase(lines, [], nothingToOrderAt),
      // §5.1 G2 — the empty state IS the content: it promises a list that arrives pre-filled from
      // the client order, not a blank page. Only a commandable line can be ordered.
      commandableCount: clientOrder.orderLines.filter((l) => isCommandable(l)).length,
      // Reuses the registre's own matching (source id, cancelled lines do not cover their source)
      // rather than re-deriving it here — two answers to « is this line tracked? » would drift.
      untracked: reconcileClientOrder(clientOrder.orderLines, lines)
        .rows.filter((r) => !r.tracked && isCommandable(r.clientLine)).length,
      entries: breakdownEntries(counts),
    }
  }, [clientOrder, lines])

  const registryPath = `/planning/projects/${projectId}/commandes`

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold">Commande fournisseurs</h2>
        <p className="text-sm text-muted-foreground">Les décisions se prennent dans le registre — ici, l'état des lieux</p>
      </div>
      <Link to={registryPath} data-testid="client-order-registry-link"
        className="shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted pointer-coarse:min-h-11 pointer-coarse:inline-flex pointer-coarse:items-center">
        Gérer dans le registre →
      </Link>
    </div>
  )

  // Keeps the block from appearing out of nowhere after two round-trips and shoving the Facturation
  // section down the page. It approximates the tallest phase, it does not pin an exact height.
  if (!failed && (!clientOrder || !lines)) {
    return (
      <section id="supplier-ordering" className="space-y-3" data-testid="client-order-picker">
        {header}
        <div className="space-y-2 rounded-lg border p-3" data-testid="client-order-loading">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-72" />
        </div>
      </section>
    )
  }

  if (failed || !clientOrder || !lines) {
    return (
      <section id="supplier-ordering" className="space-y-3" data-testid="client-order-picker">
        {header}
        {/* role="alert" so the one message saying the state could not be read is announced, and not
            only drawn — the whole point of this branch is that a dropped request stops being silent. */}
        <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border border-destructive/60 bg-destructive/5 p-3"
          data-testid="client-order-error">
          <p className="text-sm text-foreground">
            État des commandes indisponible — il n'a pas pu être lu.
          </p>
          <button type="button" onClick={retry} data-testid="client-order-retry"
            className="shrink-0 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted pointer-coarse:min-h-11">
            Réessayer
          </button>
        </div>
      </section>
    )
  }

  const { ready, nothingToOrderAt, phase, commandableCount, untracked, entries } = derived!

  return (
    <section id="supplier-ordering" className="space-y-3" data-testid="client-order-picker">
      {header}

      {/* Shown in `en-cours` and `recu` only. NOT in `a-definir`, which already carries the count in
          its own copy — and NOT in `rien`, where the marker is an explicit human decision that
          nothing needs ordering. A quote always has product lines; warning that they are "not on
          the list" would fire on every legitimate « rien à commander » project, for ever, with no
          way to acknowledge it. That is the permanent banner §5.1 G4 forbids, and the marker is
          itself the acknowledgement. Consequence, stated rather than hidden: a quote amendment that
          adds orderable lines AFTER the marker is set is no longer flagged here. Distinguishing
          that case needs the line's creation date, which the DTO does not carry; the durable fix is
          server-side, clearing the marker when the client order gains a commandable line. */}
      {(phase === 'en-cours' || phase === 'recu') && untracked > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="client-order-untracked">
          {untracked} article{untracked === 1 ? '' : 's'} du devis {untracked === 1 ? "n'est" : 'ne sont'} pas sur la liste de commande.
        </p>
      )}

      {phase === 'a-definir' && (
        <div className="rounded-lg border border-dashed p-3 text-sm" data-testid="client-order-to-define">
          {commandableCount > 0
            ? <>Rien n'est encore trié — <span className="font-medium">{commandableCount} article{commandableCount === 1 ? '' : 's'}</span> du devis {commandableCount === 1 ? 'attend' : 'attendent'} la liste de commande.</>
            : <>Rien n'est encore trié pour ce chantier.</>}
        </div>
      )}

      {phase === 'rien' && (
        <span className="inline-block rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
          data-testid="client-order-nothing-badge">
          {/* Local rendering: the marker is a UTC instant — slicing its date part would show
              yesterday for a Brussels evening click. */}
          Rien à commander — confirmé le {frDate(nothingToOrderAt!)}
        </span>
      )}

      {phase === 'recu' && (
        <p className="text-sm font-semibold text-emerald-700" data-testid="client-order-received">
          {/* « prêt pour la pose » is a claim about the whole job, so it is withheld while orderable
              quote lines are still off the list. */}
          {untracked > 0 ? 'Tout ce qui est suivi est reçu' : 'Tout reçu · prêt pour la pose'}
        </p>
      )}

      {phase === 'en-cours' && (
        <div className="rounded-lg border p-3">
          <p className="text-sm font-semibold" data-testid="client-order-readiness">
            {ready.received}/{ready.total} reçue{ready.total === 1 ? '' : 's'}
          </p>
          {/* One dense line, never a row of stat cards: DESIGN-CONTEXT §3.3 asks for dense rows,
              not stacked cards, and a card mosaic is the App UI hard-rejection pattern. */}
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground"
            data-testid="client-order-summary">
            {entries.map(([label, n], i) => (
              <span key={label}>{i > 0 ? '· ' : ''}{label} <b className="text-foreground">{n}</b></span>
            ))}
          </p>
        </div>
      )}
    </section>
  )
}
